import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft, BarChart3, Loader2, Send, MessageSquare, Trash2, User, FileSpreadsheet, ChevronDown, ChevronUp, Brain } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ANALYSIS_SECTION_ID = 'analysis-results';

const fmtNum = (v, digits = 4) => {
  if (v === null || v === undefined) return '—';
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  if (Math.abs(n) >= 1000 || (Math.abs(n) < 0.0001 && n !== 0)) return n.toExponential(3);
  return n.toFixed(digits);
};

const formatType = (t) => (t || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const isQuantAnalysis = (type) => (
  ['regression', 'correlation', 'descriptive', 'ttest', 't_test', 'anova', 'chi_square', 'ztest', 'z_test']
    .some((t) => (type || '').toLowerCase().includes(t))
);

/** Render a table from an object of rows (row label -> object of columns) */
const ObjectTable = ({ data, valueFormatter = (v) => fmtNum(v, 4) }) => {
  if (!data || typeof data !== 'object') return null;
  const rows = Object.entries(data);
  if (rows.length === 0) return <p className="text-xs text-slate-400 italic">No data</p>;
  // If the values are primitives, render a simple key/value table
  const allPrimitive = rows.every(([, v]) => typeof v !== 'object' || v === null);
  if (allPrimitive) {
    return (
      <table className="w-full text-sm border-collapse">
        <tbody>
          {rows.map(([k, v]) => (
            <tr key={k} className="border-b border-slate-100">
              <td className="py-1.5 pr-4 text-slate-600 capitalize">{k.replace(/_/g, ' ')}</td>
              <td className="py-1.5 font-mono text-slate-900">{typeof v === 'number' ? valueFormatter(v) : String(v)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }
  // Row-per-object: build column headers from first object's keys
  const firstObj = rows[0][1] || {};
  const cols = typeof firstObj === 'object' && firstObj !== null ? Object.keys(firstObj) : [];
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="bg-slate-100">
            <th className="text-left px-3 py-2 font-semibold text-slate-700">Variable</th>
            {cols.map((c) => (
              <th key={c} className="text-left px-3 py-2 font-semibold text-slate-700 capitalize">{c.replace(/_/g, ' ')}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map(([rowLabel, rowValues]) => (
            <tr key={rowLabel} className="border-b border-slate-100">
              <td className="px-3 py-1.5 text-slate-800 font-medium">{rowLabel}</td>
              {cols.map((c) => {
                const v = rowValues?.[c];
                return (
                  <td key={c} className="px-3 py-1.5 font-mono text-slate-700">
                    {v === null || v === undefined ? '—' : typeof v === 'number' ? valueFormatter(v) : String(v)}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

/** Type-aware renderer for a single analysis' full results */
const AnalysisResultsView = ({ analysis }) => {
  const r = analysis.results || {};
  const type = (analysis.analysis_type || '').toLowerCase();

  // Regression equation
  const renderRegressionEquation = () => {
    if (r.intercept === undefined || !r.coefficients) return null;
    const dv = r.dependent_variable || 'Y';
    const terms = Object.entries(r.coefficients).map(([v, c]) => {
      const n = Number(c);
      const sign = n >= 0 ? ' + ' : ' − ';
      return `${sign}${Math.abs(n).toFixed(4)} × (${v})`;
    }).join('');
    return (
      <div className="mb-4 p-3 bg-slate-900 text-slate-100 rounded-lg">
        <div className="text-xs uppercase tracking-wide text-slate-400 mb-1">Regression Equation</div>
        <div className="font-mono text-sm break-words">{`${dv} = ${Number(r.intercept).toFixed(4)}${terms}`}</div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Top-level scalar metrics */}
      <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-2">
        {r.sample_size !== undefined && (
          <div className="bg-slate-50 p-3 rounded">
            <div className="text-xs text-slate-500 uppercase tracking-wide">Sample size (n)</div>
            <div className="font-mono font-semibold text-slate-900">{r.sample_size}</div>
          </div>
        )}
        {r.r_squared !== undefined && (
          <div className="bg-slate-50 p-3 rounded">
            <div className="text-xs text-slate-500 uppercase tracking-wide">R²</div>
            <div className="font-mono font-semibold text-slate-900">{fmtNum(r.r_squared)}</div>
          </div>
        )}
        {r.adjusted_r_squared !== undefined && (
          <div className="bg-slate-50 p-3 rounded">
            <div className="text-xs text-slate-500 uppercase tracking-wide">Adj. R²</div>
            <div className="font-mono font-semibold text-slate-900">{fmtNum(r.adjusted_r_squared)}</div>
          </div>
        )}
        {r.intercept !== undefined && (
          <div className="bg-slate-50 p-3 rounded">
            <div className="text-xs text-slate-500 uppercase tracking-wide">Intercept</div>
            <div className="font-mono font-semibold text-slate-900">{fmtNum(r.intercept)}</div>
          </div>
        )}
        {r.p_value !== undefined && (
          <div className="bg-slate-50 p-3 rounded">
            <div className="text-xs text-slate-500 uppercase tracking-wide">p-value</div>
            <div className="font-mono font-semibold text-slate-900">{fmtNum(r.p_value)}</div>
          </div>
        )}
        {r.t_statistic !== undefined && (
          <div className="bg-slate-50 p-3 rounded">
            <div className="text-xs text-slate-500 uppercase tracking-wide">t</div>
            <div className="font-mono font-semibold text-slate-900">{fmtNum(r.t_statistic)}</div>
          </div>
        )}
        {r.z_statistic !== undefined && (
          <div className="bg-slate-50 p-3 rounded">
            <div className="text-xs text-slate-500 uppercase tracking-wide">z</div>
            <div className="font-mono font-semibold text-slate-900">{fmtNum(r.z_statistic)}</div>
          </div>
        )}
        {r.f_statistic !== undefined && (
          <div className="bg-slate-50 p-3 rounded">
            <div className="text-xs text-slate-500 uppercase tracking-wide">F</div>
            <div className="font-mono font-semibold text-slate-900">{fmtNum(r.f_statistic)}</div>
          </div>
        )}
        {r.chi_square !== undefined && (
          <div className="bg-slate-50 p-3 rounded">
            <div className="text-xs text-slate-500 uppercase tracking-wide">χ²</div>
            <div className="font-mono font-semibold text-slate-900">{fmtNum(r.chi_square)}</div>
          </div>
        )}
        {r.degrees_of_freedom !== undefined && (
          <div className="bg-slate-50 p-3 rounded">
            <div className="text-xs text-slate-500 uppercase tracking-wide">df</div>
            <div className="font-mono font-semibold text-slate-900">{fmtNum(r.degrees_of_freedom, 0)}</div>
          </div>
        )}
        {r.correlation_coefficient !== undefined && (
          <div className="bg-slate-50 p-3 rounded">
            <div className="text-xs text-slate-500 uppercase tracking-wide">r</div>
            <div className="font-mono font-semibold text-slate-900">{fmtNum(r.correlation_coefficient)}</div>
          </div>
        )}
        {r.effect_size !== undefined && (
          <div className="bg-slate-50 p-3 rounded">
            <div className="text-xs text-slate-500 uppercase tracking-wide">Effect size</div>
            <div className="font-mono font-semibold text-slate-900">{fmtNum(r.effect_size)}</div>
          </div>
        )}
      </div>

      {/* Regression equation */}
      {renderRegressionEquation()}

      {/* Regression coefficients table */}
      {r.coefficients && typeof r.coefficients === 'object' && (
        <div>
          <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-2">Regression Coefficients</div>
          <ObjectTable data={r.coefficients} />
        </div>
      )}

      {/* Descriptive statistics per variable */}
      {r.descriptive_statistics && (
        <div>
          <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-2">Descriptive Statistics</div>
          <ObjectTable data={r.descriptive_statistics} />
        </div>
      )}

      {/* Correlation matrix */}
      {r.correlation_matrix && (
        <div>
          <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-2">Correlation Matrix</div>
          <ObjectTable data={r.correlation_matrix} />
        </div>
      )}

      {/* ANOVA table */}
      {r.anova_table && (
        <div>
          <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-2">ANOVA Table</div>
          <ObjectTable data={r.anova_table} />
        </div>
      )}

      {/* Group statistics */}
      {r.group_statistics && (
        <div>
          <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-2">Group Statistics</div>
          <ObjectTable data={r.group_statistics} />
        </div>
      )}

      {/* Thematic / sentiment / qualitative text summaries */}
      {r.themes && Array.isArray(r.themes) && (
        <div>
          <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-2">Themes Identified</div>
          <ul className="space-y-2">
            {r.themes.map((t, i) => (
              <li key={i} className="border-l-2 border-indigo-400 pl-3 py-1 bg-slate-50">
                <div className="font-medium text-slate-900">{t.theme || t.name || `Theme ${i + 1}`}</div>
                {t.description && <p className="text-sm text-slate-600 mt-1">{t.description}</p>}
                {t.examples && Array.isArray(t.examples) && (
                  <p className="text-xs text-slate-500 mt-1 italic">Examples: {t.examples.slice(0, 3).join('; ')}</p>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
      {r.sentiment_distribution && (
        <div>
          <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-2">Sentiment Distribution</div>
          <ObjectTable data={r.sentiment_distribution} valueFormatter={(v) => fmtNum(v, 2)} />
        </div>
      )}
      {r.word_frequencies && (
        <div>
          <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-2">Top Word Frequencies</div>
          <ObjectTable data={r.word_frequencies} valueFormatter={(v) => fmtNum(v, 0)} />
        </div>
      )}

      {/* Interpretation of significance / summary statement */}
      {(r.interpretation || r.significance_interpretation || r.summary) && (
        <div className="bg-amber-50 border-l-4 border-amber-400 p-3 rounded-r">
          <div className="text-xs uppercase tracking-wide text-amber-800 font-semibold mb-1">Summary</div>
          <p className="text-sm text-amber-900 whitespace-pre-wrap">{r.interpretation || r.significance_interpretation || r.summary}</p>
        </div>
      )}

      {/* Catch-all: show any other keys we haven't rendered above so supervisor sees EVERYTHING */}
      {(() => {
        const handled = new Set([
          'sample_size','r_squared','adjusted_r_squared','intercept','p_value','t_statistic','z_statistic',
          'f_statistic','chi_square','degrees_of_freedom','correlation_coefficient','effect_size',
          'coefficients','descriptive_statistics','correlation_matrix','anova_table','group_statistics',
          'themes','sentiment_distribution','word_frequencies','interpretation','significance_interpretation','summary',
          'dependent_variable','independent_variables','chart_data','predictions','residuals',
        ]);
        const extras = Object.entries(r).filter(([k]) => !handled.has(k));
        if (extras.length === 0) return null;
        return (
          <details className="mt-2 bg-slate-50 rounded p-3">
            <summary className="cursor-pointer text-xs text-slate-600 font-semibold uppercase tracking-wide">Additional result fields ({extras.length})</summary>
            <pre className="text-xs text-slate-700 whitespace-pre-wrap mt-2 overflow-x-auto">{JSON.stringify(Object.fromEntries(extras), null, 2)}</pre>
          </details>
        );
      })()}
    </div>
  );
};

const SupervisorAnalysisReview = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const isCollaborator = user?.role === 'collaborator';

  const [loading, setLoading] = useState(true);
  const [project, setProject] = useState(null);
  const [analyses, setAnalyses] = useState([]);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [posting, setPosting] = useState(false);
  const [expandedIds, setExpandedIds] = useState({});

  useEffect(() => {
    const load = async () => {
      try {
        const [projRes, anaRes, cmtRes] = await Promise.all([
          axios.get(`${API}/projects/${projectId}`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/projects/${projectId}/analyses`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/projects/${projectId}/comments`, { headers: { Authorization: `Bearer ${token}` } })
        ]);
        setProject(projRes.data);
        const list = anaRes.data || [];
        setAnalyses(list);
        // Expand all by default so supervisor sees everything at a glance
        const initExpanded = {};
        list.forEach((a) => { initExpanded[a.id] = true; });
        setExpandedIds(initExpanded);
        setComments((cmtRes.data || []).filter((c) => c.section_id === ANALYSIS_SECTION_ID));
      } catch (err) {
        toast.error('Failed to load analyses');
        navigate(isCollaborator ? '/collaborator-dashboard' : `/project/${projectId}`);
      } finally {
        setLoading(false);
      }
    };
    if (token) load();
  }, [projectId, token, navigate, isCollaborator]);

  const toggleExpanded = (id) => setExpandedIds((prev) => ({ ...prev, [id]: !prev[id] }));

  const addComment = async () => {
    if (!newComment.trim()) {
      toast.error('Write something first');
      return;
    }
    setPosting(true);
    try {
      const res = await axios.post(
        `${API}/projects/${projectId}/comments`,
        { text: newComment.trim(), section_id: ANALYSIS_SECTION_ID },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setComments((prev) => [...prev, res.data]);
      setNewComment('');
      toast.success('Comment added — the student will see it on their Analyses tab.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add comment');
    } finally {
      setPosting(false);
    }
  };

  const deleteComment = async (commentId) => {
    if (!window.confirm('Delete this comment?')) return;
    try {
      await axios.delete(`${API}/projects/${projectId}/comments/${commentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setComments((prev) => prev.filter((c) => c.id !== commentId));
      toast.success('Comment deleted');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-rose-700 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(isCollaborator ? '/collaborator-dashboard' : `/project/${projectId}`)}
                className="text-slate-500 hover:text-slate-700"
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <h1 className="text-lg font-semibold text-slate-900">Analysis Results Review</h1>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        <div className="card-dashboard">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-indigo-100 rounded-xl">
              <BarChart3 className="w-6 h-6 text-indigo-600" />
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-serif font-semibold text-slate-900">{project?.title}</h2>
              <p className="text-sm text-slate-500 capitalize">{project?.research_type} study</p>
              <p className="text-sm text-slate-600 mt-2">
                {analyses.length} saved {analyses.length === 1 ? 'analysis' : 'analyses'}. Full results are shown below. Add general comments for the student in red.
              </p>
            </div>
          </div>
        </div>

        {isCollaborator && (
          <div className="mb-2 p-4 bg-red-50 border-2 border-red-300 rounded-xl flex items-start gap-3" data-testid="supervisor-analysis-banner">
            <MessageSquare className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-red-900">Supervisor view: Analysis Results</p>
              <p className="text-xs text-red-700 mt-0.5">
                Your comments here are shown in red on the student&apos;s Analyses tab.
              </p>
            </div>
          </div>
        )}

        {/* General comments block */}
        <div className="card-dashboard" data-testid="analysis-comments-block">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-slate-900 flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-red-600" />
              Supervisor&apos;s Comments on Analysis Results
            </h3>
            <span className="text-xs text-slate-500">{comments.length} comment{comments.length !== 1 ? 's' : ''}</span>
          </div>

          {comments.length === 0 ? (
            <p className="text-sm text-slate-500 italic mb-3">No comments yet.</p>
          ) : (
            <div className="space-y-2 mb-3">
              {comments.map((c) => (
                <div
                  key={c.id}
                  className="border-l-4 border-red-500 bg-red-50 pl-3 pr-3 py-2 rounded text-sm flex items-start justify-between gap-3"
                  data-testid={`analysis-comment-${c.id}`}
                >
                  <div className="flex-1">
                    <div className="text-xs text-red-800 font-semibold mb-0.5">
                      <MessageSquare className="inline w-3 h-3 mr-1" />
                      {c.author_name || c.author_email} · {new Date(c.created_at).toLocaleString()}
                    </div>
                    <p className="text-red-700 font-medium whitespace-pre-wrap">{c.text}</p>
                  </div>
                  {user?.id === c.author_id && (
                    <button
                      onClick={() => deleteComment(c.id)}
                      className="text-red-400 hover:text-red-700 shrink-0"
                      title="Delete"
                      data-testid={`delete-analysis-comment-${c.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {isCollaborator && (
            <div className="p-3 bg-red-50/60 border border-red-200 rounded-lg">
              <div className="flex items-center gap-2 text-xs text-red-800 font-semibold mb-2">
                <MessageSquare className="w-3.5 h-3.5" />
                Add a general comment on the analysis results
              </div>
              <div className="flex gap-2">
                <textarea
                  rows={3}
                  className="flex-1 text-sm rounded border border-red-300 bg-white px-2 py-1.5 text-red-700 placeholder:text-red-300 focus:border-red-500 focus:outline-none"
                  placeholder="E.g. 'Your R² is high but check multicollinearity between X1 and X2...'"
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  data-testid="analysis-comment-textarea"
                />
                <Button
                  size="sm"
                  onClick={addComment}
                  disabled={posting}
                  className="bg-red-600 hover:bg-red-700 text-white shrink-0 self-start"
                  data-testid="add-analysis-comment-btn"
                >
                  {posting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Analyses list */}
        <div className="card-dashboard">
          <h3 className="font-semibold text-slate-900 mb-4">Saved Analyses ({analyses.length})</h3>
          {analyses.length === 0 ? (
            <div className="text-center py-10 text-slate-500">
              <FileSpreadsheet className="w-10 h-10 mx-auto mb-2 text-slate-300" />
              <p className="text-sm">No analyses have been run on this project yet.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {analyses.map((a) => {
                const expanded = expandedIds[a.id];
                return (
                  <div key={a.id} className="border border-slate-200 rounded-lg overflow-hidden" data-testid={`analysis-item-${a.id}`}>
                    <button
                      className="w-full px-4 py-3 bg-slate-50 hover:bg-slate-100 flex items-start justify-between gap-3 text-left"
                      onClick={() => toggleExpanded(a.id)}
                      data-testid={`toggle-analysis-${a.id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-semibold text-slate-900">{formatType(a.analysis_type)}</h4>
                          <span className="px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded-full text-xs font-medium shrink-0">
                            {isQuantAnalysis(a.analysis_type) ? 'Quantitative' : 'Qualitative'}
                          </span>
                          {a.interpretation && (
                            <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-medium shrink-0">
                              Has interpretation
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-slate-500">
                          <User className="inline w-3 h-3 mr-1" />
                          Run on {new Date(a.created_at).toLocaleString()}
                        </p>
                        {a.variables && a.variables.length > 0 && (
                          <div className="text-xs text-slate-600 mt-1">
                            Variables: <span className="font-mono">{a.variables.join(', ')}</span>
                          </div>
                        )}
                      </div>
                      <div className="shrink-0 pt-1">
                        {expanded ? <ChevronUp className="w-5 h-5 text-slate-500" /> : <ChevronDown className="w-5 h-5 text-slate-500" />}
                      </div>
                    </button>

                    {expanded && (
                      <div className="p-4 space-y-4">
                        {/* Full results view */}
                        <AnalysisResultsView analysis={a} />

                        {/* Student's saved interpretation (if any) */}
                        {a.interpretation && (
                          <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-lg p-4">
                            <div className="flex items-center gap-2 mb-2">
                              <Brain className="w-4 h-4 text-indigo-600" />
                              <p className="text-sm font-medium text-indigo-900">Student&apos;s saved interpretation</p>
                            </div>
                            <p className="text-sm text-slate-700 whitespace-pre-line">{a.interpretation}</p>
                            {a.interpretation_saved_at && (
                              <p className="text-xs text-slate-400 mt-2">
                                Saved: {new Date(a.interpretation_saved_at).toLocaleString()}
                              </p>
                            )}
                          </div>
                        )}
                        {!a.interpretation && (
                          <p className="text-xs text-slate-400 italic">Student has not written an interpretation for this analysis.</p>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default SupervisorAnalysisReview;
